// src/components/founderDashboard/TodayDashboard.tsx
// Burst HQ > Brief: James's morning brief. The nightly agent (assistant/ in the
// repo, on his Mac) uploads one brief a day to /founder-portal/briefs/upload/;
// this page reads it, and saves his ratings, notes, ideas, "done" and
// "handled" ticks straight back so the next night's run can learn from them.
// Numbers are as they were at the time of the run (frozen), so an earlier day
// reads as it did then. Layout follows the Oct 2026 "Burst HQ - Brief" handoff:
// Today, Issues, News, Moments and Suggestions under one sticky jump nav.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "./apiClient";
import { C, Empty, FONT, FONT_NUM, Shell, numStyle } from "./hq";

// ---------- shapes (see assistant/prompt.md for what the agent writes) ----------
type Metric = { value: number; avg7: number; delta: number; signal: boolean };
type Story = { id: string; title: string; url?: string; source?: string; published?: string; first_seen?: string;
    body?: string; shape?: string; quote?: string; film?: string; meta?: string };
type NewsCategory = { category: string; stories: Story[]; note?: string };
type Item = { tag?: string; title: string; body?: string; link?: string; label?: string };
type Issue = { user_id: number | null; name: string; platform: string; app_version: string;
    failures: { what: string; count: number; statuses: number[]; first: string; last: string; path: string }[];
    crashes: { kind: string; at: string; screen: string; message: string; fatal: boolean }[];
    screens_before: string[]; total: number };
type Brief = {
    kind?: string; status_note?: string; pulse_read?: string; cohort_note?: string; ops_note?: string;
    do_this_first?: { text: string; chip?: string; link?: string; link_label?: string };
    needs?: Item[]; today?: { time: string; title: string; note?: string }[];
    issue_notes?: Record<string, string>; issues_note?: string;
    news?: NewsCategory[]; news_scanned?: number; radar?: { scanned?: number; stories: Story[] };
    moments?: Story[]; replies?: { to: string; body: string }[]; waiting?: Item[];
    extra_loaded?: string[]; extra_missing?: string[];
};
type Ratings = Record<string, { rating: string; why: string }>;
type Day = { date: string | null; brief: Brief | null; numbers?: any; status?: any; health?: string;
    dates: string[]; ratings?: Ratings; first_done?: boolean; handled?: string[] };
type Idea = { id: number; kind: "content" | "product"; text: string; status: string; source: string;
    source_title: string; source_url: string; created_at: string; updated_at: string };
type Sev = "high" | "med" | "low";
type IssueRow = { id: string; scope: "System" | "Members"; who: string; kind: string; title: string; detail: string;
    count: number; platform: string; last: string; sev: Sev; person?: Issue };

// ---------- tokens from the handoff ----------
const INK = C.ink;
const AMBER = "#D9A93E";
const SEV: Record<Sev, [string, string]> = {
    high: [C.alert, "rgba(169,71,47,.14)"],
    med: [AMBER, "rgba(217,169,62,.18)"],
    low: [C.other, "rgba(139,157,175,.18)"],
};
const SOURCE_NAMES: Record<string, string> = { prod: "App numbers", celery: "Celery check", commits: "Commits", chats: "Your Claude chats" };
const card: React.CSSProperties = {
    background: "#FFF", borderRadius: 18, boxShadow: "0 8px 24px rgba(22,25,37,.06)", padding: "22px 24px",
    display: "flex", flexDirection: "column", minWidth: 0,
};
const label = (color = "rgba(22,25,37,.6)", size = 11): React.CSSProperties => ({
    font: `600 ${size}px ${FONT}`, letterSpacing: size === 10 ? ".12em" : ".14em", textTransform: "uppercase", color,
});
const eyebrow: React.CSSProperties = { font: `600 11px ${FONT}`, letterSpacing: ".12em", textTransform: "uppercase", color: C.sageD };
const h2: React.CSSProperties = { margin: 0, font: `800 26px ${FONT}`, letterSpacing: "-.015em" };
const tagChip = (bg: string, fg: string): React.CSSProperties => ({
    font: `700 10px ${FONT}`, letterSpacing: ".12em", textTransform: "uppercase", padding: "4px 9px", borderRadius: 6, background: bg, color: fg,
});
const hair = "1px solid rgba(22,25,37,.08)";
const reset: React.CSSProperties = { border: 0, background: "none", padding: 0, margin: 0, font: "inherit", color: "inherit", cursor: "pointer" };

const PAGE_CSS = `
.tb a{color:${INK}}
.tb a:hover{color:${C.sageD}}
.tb input::placeholder{color:rgba(22,25,37,.45)}
.tb-grid{display:grid;gap:20px}
.tb-today1,.tb-sugs{grid-template-columns:minmax(0,2fr) minmax(300px,1fr);align-items:stretch}
.tb-sugs{align-items:start}
.tb-today3{grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr))}
.tb-moments{grid-template-columns:repeat(auto-fit,minmax(min(100%,440px),1fr))}
@media (max-width:1179px){.tb-today1,.tb-sugs{grid-template-columns:minmax(0,1fr)}.tb-sources{padding-top:0!important}}
.tb-pulse{display:grid;grid-template-columns:minmax(220px,1.3fr) repeat(auto-fit,minmax(130px,1fr))}
.tb-pulse>div+div{border-left:1px solid rgba(22,25,37,.08)}
.tb-row:hover{background:${C.inset}}
.tb-quiet{border:0;background:none;cursor:pointer;font:600 12px ${FONT};padding:6px 10px;border-radius:8px;color:rgba(22,25,37,.65)}
.tb-quiet:hover{background:${C.panel};color:${INK}}
.tb-quiet.drop:hover{background:rgba(169,71,47,.1);color:${C.alert}}
.tb-issues{display:grid;grid-template-columns:28px 170px minmax(0,1fr) 150px 80px 90px 120px;gap:12px;align-items:center;padding:13px 22px}
.tb-section{display:flex;flex-direction:column;gap:16px;padding-top:20px;scroll-margin-top:64px}
@media (max-width:900px){
  .tb-pulse{grid-template-columns:1fr 1fr}
  .tb-pulse>div:first-child{grid-column:span 2}
  .tb-pulse>div+div{border-left:0;border-top:1px solid rgba(22,25,37,.08)}
  .tb-pulse>div:nth-child(odd):not(:first-child){border-left:1px solid rgba(22,25,37,.08)}
  .tb-dofirst{padding:22px 20px!important}
  .tb-dofirst-text{font-size:21px!important}
  .tb-story{grid-template-columns:minmax(0,1fr)!important;padding:20px 18px!important}
  .tb-story-rate{padding-top:0!important}
  .tb-sticky{flex-wrap:nowrap!important;overflow-x:auto;scrollbar-width:none}
  .tb-sticky::-webkit-scrollbar{display:none}
}
`;

const dayLabel = (d: string, opts: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long" }) =>
    new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { ...opts, timeZone: "UTC" });
const shortDay = (d: string) => dayLabel(d, { weekday: "short", day: "numeric", month: "short" });
const dayBefore = (d: string) => new Date(new Date(`${d}T12:00:00Z`).getTime() - 864e5).toISOString().slice(0, 10);
// Card ids start with the day they first ran ("2026-10-29-n-slug"); older briefs
// used bare slugs, which get the day's date put in front, as the old page did.
const cardId = (id: string, date: string) => (/^\d{4}-\d{2}-\d{2}-/.test(id) ? id : `${date}-${id}`);
const unquote = (q: string) => q.replace(/^["“]+|["”]+$/g, "");
const read = (k: string, fallback: string) => { try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* fine */ } };

// ---------- small parts ----------
const SectionHead: React.FC<{ kicker: string; title: string; right?: React.ReactNode }> = ({ kicker, title, right }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={eyebrow}>{kicker}</div>
            <h2 style={h2}>{title}</h2>
        </div>
        {right}
    </div>
);

const Aside: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{ font: `400 12px ${FONT}`, color: C.muted }}>{children}</div>
);

// The brand toggle: white, ink border, sage active segment, a count on each.
const Toggle: React.FC<{ options: [string, number][]; value: string; onChange: (v: string) => void }> = ({ options, value, onChange }) => (
    <div style={{ display: "flex", background: "#FFF", border: `1.5px solid ${INK}`, borderRadius: 14, padding: 4, gap: 4 }}>
        {options.map(([o, n]) => {
            const on = o === value;
            return (
                <button key={o} type="button" onClick={() => onChange(o)} aria-pressed={on} style={{
                    ...reset, padding: "7px 14px", borderRadius: 10, display: "flex", gap: 6, alignItems: "baseline",
                    font: `${on ? 700 : 600} 13px ${FONT}`, background: on ? C.sage : "transparent", color: on ? "#FFF" : "rgba(22,25,37,.6)",
                }}>{o}<span style={{ font: `600 14px ${FONT_NUM}` }}>{n}</span></button>
            );
        })}
    </div>
);

const Dot: React.FC<{ color: string; size?: number; style?: React.CSSProperties }> = ({ color, size = 7, style }) => (
    <span style={{ width: size, height: size, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block", ...style }} />
);

// Useful / Not for me. Tapping the active choice again clears it.
const RateButtons: React.FC<{ rating: string; onRate: (r: string) => void }> = ({ rating, onRate }) => {
    const b = (on: boolean, fill: string): React.CSSProperties => ({
        ...reset, font: `600 12px ${FONT}`, padding: "7px 14px", borderRadius: 999, whiteSpace: "nowrap",
        border: `1px solid ${on ? fill : "rgba(22,25,37,.2)"}`, background: on ? fill : "transparent", color: on ? "#FFF" : INK,
    });
    return (
        <>
            <button type="button" aria-pressed={rating === "useful"} style={b(rating === "useful", C.sage)}
                onClick={() => onRate(rating === "useful" ? "" : "useful")}>{rating === "useful" ? "✓ Useful" : "Useful"}</button>
            <button type="button" aria-pressed={rating === "not_useful"} style={b(rating === "not_useful", INK)}
                onClick={() => onRate(rating === "not_useful" ? "" : "not_useful")}>{rating === "not_useful" ? "✕ Not for me" : "Not for me"}</button>
        </>
    );
};

// One rateable card's state: the rating saves on tap, the note as he types
// (700ms after he stops) and when he leaves the box or the page. No submit.
function useRating(story: Story, id: string, kind: "news" | "moment", category: string, saved: Ratings[string] | undefined,
    onRated: (id: string, rating: string) => void) {
    const [rating, setRating] = useState(saved?.rating || "");
    const [why, setWhy] = useState(saved?.why || "");
    const [err, setErr] = useState("");
    const timer = useRef<number | null>(null);
    const stored = useRef(!!saved);
    const latest = useRef({ rating, why });
    latest.current = { rating, why };

    const save = useCallback(() => {
        if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
        const { rating: r, why: w } = latest.current;
        if (!r && !w.trim() && !stored.current) return;
        stored.current = true;
        api.put(`/founder-portal/briefs/ratings/${encodeURIComponent(id)}/`, {
            rating: r, why: w.trim(), kind, category, title: story.title, url: story.url || "",
        }).then(() => setErr("")).catch(() => setErr("Couldn't save, it will retry when you next type or tap."));
    }, [id, story, kind, category]);

    useEffect(() => {
        const flush = () => { if (timer.current) save(); };
        const onHide = () => { if (document.visibilityState === "hidden") flush(); };
        document.addEventListener("visibilitychange", onHide);
        window.addEventListener("pagehide", flush);
        return () => { flush(); document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", flush); };
    }, [save]);

    const rate = (r: string) => { setRating(r); latest.current.rating = r; onRated(id, r); save(); };
    const noteProps = {
        value: why, "aria-label": "Note", placeholder: "Add a note…",
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            setWhy(e.target.value);
            if (timer.current) window.clearTimeout(timer.current);
            timer.current = window.setTimeout(save, 700);
        },
        onBlur: () => { if (timer.current) save(); },
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); },
    };
    return { rating, rate, noteProps, err };
}

const StoryRow: React.FC<{ story: Story; category: string; date: string; ratings: Ratings; onRated: (id: string, r: string) => void }> =
    ({ story, category, date, ratings, onRated }) => {
        const id = cardId(story.id, date);
        const { rating, rate, noteProps, err } = useRating(story, id, "news", category, ratings[id], onRated);
        const carried = story.first_seen && story.first_seen !== date;
        return (
            <article className="tb-story" style={{
                display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "16px 32px", padding: "24px 28px",
                borderBottom: hair, alignItems: "start",
            }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, maxWidth: 780 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", font: `500 12px ${FONT}`, color: "rgba(22,25,37,.5)" }}>
                        <span style={label(C.sageD, 10)}>{category}</span>
                        <span>{[story.source, story.published].filter(Boolean).join(" · ")}</span>
                        {carried && <span>· since {shortDay(story.first_seen as string)}</span>}
                    </div>
                    <h3 style={{ margin: 0, font: `700 19px/1.3 ${FONT}`, letterSpacing: "-.01em", textWrap: "pretty" } as React.CSSProperties}>
                        {story.url ? <a href={story.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>{story.title}</a> : story.title}
                    </h3>
                    {story.body && <p style={{ margin: 0, font: `400 15px/1.6 ${FONT}`, color: "rgba(22,25,37,.7)" }}>{story.body}</p>}
                    <input {...noteProps} style={{
                        marginTop: 4, font: `400 13px ${FONT}`, color: INK, background: C.inset, border: "none",
                        borderRadius: 10, padding: "9px 12px", outline: "none", maxWidth: 520, width: "100%",
                    }} />
                    {err && <span style={{ fontSize: 12, color: C.alert }}>{err}</span>}
                </div>
                <div className="tb-story-rate" style={{ display: "flex", gap: 6, paddingTop: 22 }}>
                    <RateButtons rating={rating} onRate={rate} />
                </div>
            </article>
        );
    };

const MomentCard: React.FC<{ story: Story; date: string; ratings: Ratings; onRated: (id: string, r: string) => void }> =
    ({ story, date, ratings, onRated }) => {
        const id = cardId(story.id, date);
        const { rating, rate, noteProps, err } = useRating(story, id, "moment", "", ratings[id], onRated);
        return (
            <div style={{ ...card, padding: "24px 26px", gap: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                    <span style={tagChip(C.panel, "rgba(22,25,37,.75)")}>{story.shape || "Moment"}</span>
                    <span style={{ font: `500 12px ${FONT}`, color: "rgba(22,25,37,.5)" }}>{story.meta}</span>
                </div>
                <div style={{ font: `700 18px/1.3 ${FONT}`, letterSpacing: "-.01em" }}>
                    {story.url ? <a href={story.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>{story.title}</a> : story.title}
                </div>
                {story.quote && <div style={{ font: `italic 400 14px/1.55 ${FONT}`, color: "rgba(22,25,37,.6)" }}>“{unquote(story.quote)}”</div>}
                {(story.film || story.body) && (
                    <div style={{ display: "grid", gridTemplateColumns: "3px minmax(0,1fr)", gap: 14, flex: 1 }}>
                        <div style={{ background: C.sage, borderRadius: 3 }} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <div style={label(C.sageD, 10)}>The idea</div>
                            <div style={{ font: `400 14px/1.55 ${FONT}` }}>{story.film || story.body}</div>
                        </div>
                    </div>
                )}
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", paddingTop: 12, borderTop: hair }}>
                    <RateButtons rating={rating} onRate={rate} />
                    <input {...noteProps} style={{
                        flex: 1, minWidth: 140, font: `400 12px ${FONT}`, color: INK, background: "transparent", border: "none",
                        borderBottom: "1px solid rgba(22,25,37,.2)", padding: "7px 2px", outline: "none",
                    }} />
                </div>
                {err && <span style={{ fontSize: 12, color: C.alert }}>{err}</span>}
            </div>
        );
    };

const Reply: React.FC<{ to: string; body: string }> = ({ to, body }) => {
    const [copied, setCopied] = useState(false);
    const ref = useRef<HTMLPreElement>(null);
    const copy = () => {
        navigator.clipboard?.writeText(body).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1600); })
            .catch(() => { const r = document.createRange(); if (ref.current) { r.selectNodeContents(ref.current); const s = window.getSelection(); s?.removeAllRanges(); s?.addRange(r); } });
    };
    return (
        <div style={{ padding: "12px 0", borderTop: hair, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ font: `700 14px ${FONT}` }}>To {to}</div>
            <pre ref={ref} style={{ whiteSpace: "pre-wrap", margin: 0, font: `400 13px/1.5 ${FONT}`, color: "rgba(22,25,37,.7)" }}>{body}</pre>
            <div>
                <button type="button" onClick={copy} style={{ ...reset, font: `700 12px ${FONT}`, padding: "7px 14px", borderRadius: 999, background: C.sage, color: "#FFF" }}>
                    {copied ? "Copied ✓" : "Copy reply"}
                </button>
            </div>
        </div>
    );
};

// ---------- issues ----------
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const PLATFORM: Record<string, string> = { ios: "iOS", android: "Android", web: "Web" };

// One row per person: the thing that failed most is the headline; the rest,
// the screens before and the agent's note open underneath.
function personRow(p: Issue, date: string): IssueRow {
    const top = p.failures[0];
    const crash = p.crashes[0];
    const more = p.failures.length + p.crashes.length - 1;
    const title = top ? `Couldn't ${lower(top.what)}` : crash.kind;
    const detail = top
        ? `${top.count}× · ${top.statuses.join(", ")} · ${top.first === top.last ? top.first : `${top.first} to ${top.last}`}`
        : [crash.at, crash.screen && `on ${crash.screen}`].filter(Boolean).join(" ");
    const times = [...p.failures.map(f => f.last), ...p.crashes.map(c => c.at)].sort();
    return {
        id: `${date}-issue-${p.user_id ? `u${p.user_id}` : `x${p.name}`.replace(/[^a-z0-9]/gi, "")}`,
        scope: "Members", who: p.name || "Unknown",
        kind: [p.user_id ? `User ${p.user_id}` : "", p.app_version ? `v${p.app_version}` : ""].filter(Boolean).join(" · ") || "Member",
        title, detail: more > 0 ? `${detail} · +${more} more` : detail,
        count: p.total, platform: PLATFORM[p.platform?.toLowerCase()] || p.platform || "–", last: times[times.length - 1] || "–",
        sev: p.crashes.length || p.total >= 10 ? "high" : p.total >= 3 ? "med" : "low", person: p,
    };
}

/** Pushes that should have gone out and didn't. Days stored before the API
 *  split failed from unreachable (30 Oct 2026) fall back to the old 98% rule. */
function pushFailures(n: any): number {
    if (!n?.due) return 0;
    if (typeof n.failed === "number") return n.failed;
    return n.sent < n.due * 0.98 ? n.due - n.sent : 0;
}

function pushNote(n: any): string {
    const sent = `${n.sent} of ${n.due} sent`;
    return n.unreachable ? `${sent}, ${n.unreachable} can't be reached` : sent;
}

function systemRows(day: Day, b: Brief): IssueRow[] {
    const date = day.date as string;
    const rows: IssueRow[] = [];
    const sys = (key: string, who: string, title: string, detail: string, sev: Sev, count = 1) =>
        rows.push({ id: `${date}-issue-s-${key}`, scope: "System", who, kind: "Brief data source", title, detail, count, platform: "Server", last: "overnight", sev });
    Object.entries(day.status?.sources || {}).forEach(([k, v]: any) => {
        if (v.ok) return;
        if (k === "prod") sys(k, "App numbers", /timed? ?out/i.test(v.error || "") ? "Read timed out overnight" : "Didn't load overnight",
            "No pulse, new users, issues or ops in this brief", "high");
        else sys(k, SOURCE_NAMES[k] || k, "Missing from this brief", (v.error || "Source did not return before the brief was written").slice(0, 140), "med");
    });
    (b.extra_missing || []).forEach(name => sys(name.replace(/[^a-z0-9]/gi, "").toLowerCase(), name, "Missing from this brief", "Source did not return before the brief was written", "med"));
    const celery = day.numbers?.celery;
    if (celery && !celery.up) sys("celery-down", "Celery worker", "Worker probe failed", `Probe returned ${celery.status}`, "high");
    // Only pushes that could have gone out and didn't. People with no token or
    // notifications off are skipped by design and shown as a note on Ops instead.
    const pushes = day.numbers?.health?.notifications;
    const failed = pushFailures(pushes);
    if (failed) {
        const types = Object.entries((pushes.failed_by_type ?? pushes.unsent_by_type) || {}).map(([t, n]) => `${t} ${n}`).join(", ");
        sys("pushes", "Push notifications", `${failed} of ${pushes.due} failed to send`, types || "Due but never sent", failed >= 10 ? "high" : "med", failed);
    }
    const garmin = day.numbers?.health?.garmin_reauth;
    if (garmin?.new_on_day) sys("garmin", "Garmin", `${garmin.new_on_day} new reauth ${garmin.new_on_day === 1 ? "flag" : "flags"}`, `${garmin.flagged} accounts need reauth in total`, "low", garmin.new_on_day);
    return rows;
}

const IssueDetail: React.FC<{ p: Issue; note?: string }> = ({ p, note }) => (
    <div style={{ padding: "4px 22px 18px 62px", display: "flex", flexDirection: "column", gap: 6, font: `400 13px/1.5 ${FONT}`, color: "rgba(22,25,37,.75)" }}>
        {p.failures.map((f, j) => (
            <div key={j}>Tried to <b style={{ color: INK }}>{lower(f.what)}</b>: failed {f.count}× ({f.statuses.join(", ")}) {f.first === f.last ? `at ${f.first}` : `${f.first} to ${f.last}`}</div>
        ))}
        {p.crashes.map((c, j) => (
            <div key={`c${j}`}><b style={{ color: INK }}>{c.kind}</b> at {c.at}{c.screen ? ` on ${c.screen}` : ""}{c.message ? `: ${c.message}` : ""}</div>
        ))}
        {!!p.screens_before.length && <div style={{ color: C.muted }}>Before that: {p.screens_before.join(" → ")}</div>}
        {note && <div style={{ marginTop: 4, background: C.inset, borderRadius: 10, padding: "9px 12px", color: INK, maxWidth: 780 }}>{note}</div>}
    </div>
);

const Issues: React.FC<{ day: Day; b: Brief; handled: Set<string>; onHandle: (id: string) => void }> = ({ day, b, handled, onHandle }) => {
    const [tab, setTab] = useState("All");
    const [openRow, setOpenRow] = useState<string | null>(null);
    const n = day.numbers || {};
    const rows = useMemo(() => [
        ...systemRows(day, b),
        ...((n.issues?.people || []) as Issue[]).map(p => personRow(p, day.date as string)),
    ], [day, b, n.issues]);
    const shown = rows.filter(r => tab === "All" || r.scope === tab);
    const open = rows.filter(r => !handled.has(r.id));
    const maxC = Math.max(1, ...rows.map(r => r.count));
    const kpis: [string, number | string, string][] = [
        ["Open issues", open.length, open.length ? C.alert : C.sageD],
        ["High severity", open.filter(r => r.sev === "high").length, C.alert],
        ["Members affected", rows.filter(r => r.scope === "Members").length, INK],
        ["Errors logged", n.issues ? n.issues.failed_calls + n.issues.crashes : "–", INK],
    ];
    const count = (s: string) => rows.filter(r => s === "All" || r.scope === s).length;
    return (
        <section id="issues" className="tb-section" style={{ paddingTop: 12 }}>
            <SectionHead kicker="Issues" title="What broke yesterday"
                right={<Toggle options={["All", "System", "Members"].map(s => [s, count(s)])} value={tab} onChange={setTab} />} />
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", borderBottom: hair }}>
                    {kpis.map(([l, v, c], i) => (
                        <div key={l} style={{ padding: "16px 22px", borderLeft: i ? hair : "none", display: "flex", flexDirection: "column", gap: 4 }}>
                            <div style={label("rgba(22,25,37,.55)", 10)}>{l}</div>
                            <div style={{ ...numStyle(30), lineHeight: 1, color: c }}>{v}</div>
                        </div>
                    ))}
                </div>
                {shown.length ? (
                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: 860, display: "flex", flexDirection: "column" }}>
                            <div className="tb-issues" style={{ padding: "12px 22px", ...label("rgba(22,25,37,.5)", 10), letterSpacing: ".1em", borderBottom: `1.5px solid ${INK}` }}>
                                <div /><div>Who</div><div>What happened</div><div>Occurrences</div><div>Platform</div><div>Last seen</div><div style={{ textAlign: "right" }}>Status</div>
                            </div>
                            {shown.map(r => {
                                const h = handled.has(r.id);
                                const [sev, halo] = SEV[r.sev];
                                const expandable = !!r.person;
                                const isOpen = openRow === r.id;
                                return (
                                    <div key={r.id} style={{ borderBottom: "1px solid rgba(22,25,37,.07)", opacity: h ? 0.45 : 1 }}>
                                        <div className="tb-issues tb-row" onClick={expandable ? () => setOpenRow(isOpen ? null : r.id) : undefined}
                                            style={{ cursor: expandable ? "pointer" : "default" }} aria-expanded={expandable ? isOpen : undefined}>
                                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: sev, boxShadow: `0 0 0 4px ${halo}` }} />
                                            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                                                <span style={{ font: `700 13px ${FONT}` }}>{r.who}{expandable && <span style={{ color: C.faint, marginLeft: 6 }}>{isOpen ? "▾" : "▸"}</span>}</span>
                                                <span style={{ font: `400 11px ${FONT}`, color: "rgba(22,25,37,.5)" }}>{r.kind}</span>
                                            </div>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                                                <span style={{ font: `600 13px ${FONT}` }}>{r.title}</span>
                                                <span style={{ font: `400 12px/1.45 ${FONT}`, color: "rgba(22,25,37,.6)" }}>{r.detail}</span>
                                            </div>
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                <div style={{ flex: 1, height: 6, borderRadius: 6, background: C.track }}>
                                                    <div style={{ height: 6, borderRadius: 6, background: sev, width: `${(r.count / maxC) * 100}%` }} />
                                                </div>
                                                <span style={{ ...numStyle(16), width: 26, textAlign: "right" }}>{r.count}</span>
                                            </div>
                                            <div style={{ font: `400 13px ${FONT}`, color: "rgba(22,25,37,.7)" }}>{r.platform}</div>
                                            <div style={{ ...numStyle(15, 500), color: "rgba(22,25,37,.7)" }}>{r.last}</div>
                                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <button type="button" onClick={e => { e.stopPropagation(); onHandle(r.id); }} style={{
                                                    ...reset, font: `600 12px ${FONT}`, padding: "6px 12px", borderRadius: 999,
                                                    border: `1px solid ${h ? "transparent" : "rgba(22,25,37,.2)"}`, background: h ? C.panel : "#FFF", color: INK,
                                                }}>{h ? "✓ Handled" : "Mark handled"}</button>
                                            </div>
                                        </div>
                                        {isOpen && r.person && <IssueDetail p={r.person} note={r.person.user_id ? b.issue_notes?.[String(r.person.user_id)] : undefined} />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: 22, font: `400 13px ${FONT}`, color: C.muted }}>
                        {rows.length ? `Nothing in ${tab}.` : n.issues ? "Nothing broke yesterday." : "Issues need the app numbers, which didn't load."}
                    </div>
                )}
                {(b.issues_note || n.issues) && (
                    <div style={{ padding: "12px 22px", background: C.inset, font: `400 12px/1.5 ${FONT}`, color: "rgba(22,25,37,.6)" }}>
                        {b.issues_note || `${n.issues.failed_calls} failed calls and ${n.issues.crashes} crashes from real users, from the request log. Expected answers (401, 402, 409) aren't counted.`}
                    </div>
                )}
            </div>
        </section>
    );
};

// ---------- suggestions ----------
const WEEK = 7 * 864e5;

const Suggestions: React.FC<{ onOpenCount: (n: number) => void }> = ({ onOpenCount }) => {
    const [ideas, setIdeas] = useState<Idea[] | null>(null);
    const [tab, setTab] = useState<"Content" | "Product">(read("hq-ideas-tab", "content") === "product" ? "Product" : "Content");
    const [draft, setDraft] = useState("");
    const [err, setErr] = useState("");
    const draftId = useRef<number | null>(null);
    const timer = useRef<number | null>(null);
    const chain = useRef<Promise<any>>(Promise.resolve());
    const kind = tab === "Content" ? "content" : "product";

    const load = useCallback(() => {
        api.get("/founder-portal/briefs/suggestions/", { params: { status: "all" } })
            .then(r => setIdeas(r.data)).catch(() => setErr("Couldn't load ideas."));
    }, []);
    useEffect(load, [load]);

    // Open ideas, plus anything ticked off in the last week so a tick can be undone.
    const live = (ideas || []).filter(i => i.status === "open" || (i.status === "done" && Date.now() - Date.parse(i.updated_at) < WEEK));
    const openCount = (k: string) => live.filter(i => i.kind === k && i.status === "open").length;
    const openTotal = live.filter(i => i.status === "open").length;
    useEffect(() => { if (ideas) onOpenCount(openTotal); }, [ideas, openTotal, onOpenCount]);

    // The idea saves as he types (POST, then PATCH, DELETE if emptied); Enter or
    // Add just clears the box for the next one.
    const saveDraft = useCallback((text: string) => {
        if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
        const t = text.trim();
        chain.current = chain.current.then(async () => {
            if (!draftId.current && t) {
                const r = await api.post("/founder-portal/briefs/suggestions/", { kind, text: t });
                draftId.current = r.data.id;
            } else if (draftId.current && t) {
                await api.patch(`/founder-portal/briefs/suggestions/${draftId.current}/`, { text: t });
            } else if (draftId.current && !t) {
                await api.delete(`/founder-portal/briefs/suggestions/${draftId.current}/`);
                draftId.current = null;
            }
        }).catch(() => setErr("Couldn't save that idea. Try again in a moment."));
        return chain.current;
    }, [kind]);

    const commit = () => {
        if (!draft.trim() && !draftId.current) return;
        const p = timer.current ? saveDraft(draft) : chain.current;
        setDraft("");
        p.then(() => { draftId.current = null; load(); });
    };
    const act = (id: number, patch: object) => {
        setIdeas(cur => (cur || []).map(i => (i.id === id ? { ...i, ...patch, updated_at: new Date().toISOString() } : i)));
        api.patch(`/founder-portal/briefs/suggestions/${id}/`, patch).catch(() => { setErr("Couldn't update that idea."); load(); });
    };

    const shown = live.filter(i => i.kind === kind && i.id !== draftId.current);
    const other = kind === "content" ? "product" : "content";
    return (
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            <SectionHead kicker="Suggestions" title="Your idea list"
                right={<Toggle options={[["Content", openCount("content")], ["Product", openCount("product")]]} value={tab}
                    onChange={v => { commit(); setTab(v as "Content" | "Product"); write("hq-ideas-tab", v.toLowerCase()); }} />} />
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                {shown.map(i => {
                    const done = i.status === "done";
                    return (
                        <div key={i.id} className="tb-row" style={{ display: "grid", gridTemplateColumns: "28px minmax(0,1fr) auto", gap: 16, alignItems: "center", padding: "18px 22px", borderBottom: "1px solid rgba(22,25,37,.07)" }}>
                            <button type="button" title={done ? "Undo" : "Mark done"} aria-pressed={done} onClick={() => act(i.id, { status: done ? "open" : "done" })} style={{
                                ...reset, width: 24, height: 24, borderRadius: "50%", border: `1.5px solid ${done ? C.sage : "rgba(22,25,37,.3)"}`,
                                background: done ? C.sage : "#FFF", color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", font: `700 12px ${FONT}`,
                            }}>{done ? "✓" : ""}</button>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                                <div style={{ font: `600 15px/1.4 ${FONT}`, overflowWrap: "anywhere", textDecoration: done ? "line-through" : "none", color: done ? "rgba(22,25,37,.4)" : INK }}>{i.text}</div>
                                <div style={{ font: `400 12px ${FONT}`, color: "rgba(22,25,37,.5)" }}>
                                    {dayLabel(i.created_at.slice(0, 10), { day: "numeric", month: "short" })} · {i.source === "note" && i.source_title
                                        ? <>from your note on {i.source_url ? <a href={i.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>“{i.source_title}”</a> : `“${i.source_title}”`}</>
                                        : "added by you"}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 4 }}>
                                <button type="button" className="tb-quiet" onClick={() => act(i.id, { kind: other })}>Move to {other === "content" ? "Content" : "Product"}</button>
                                <button type="button" className="tb-quiet drop" onClick={() => act(i.id, { status: "dropped" })}>Drop</button>
                            </div>
                        </div>
                    );
                })}
                {ideas && !shown.length && (
                    <div style={{ padding: 22, font: `400 13px ${FONT}`, color: C.muted }}>No {kind} ideas yet. Add one below, or write one into a note on a story.</div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "28px minmax(0,1fr) auto", gap: 16, alignItems: "center", padding: "14px 22px", background: C.inset }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", border: "1.5px dashed rgba(22,25,37,.3)", display: "flex", alignItems: "center", justifyContent: "center", font: `600 14px ${FONT}`, color: C.faint }}>+</div>
                    <input value={draft} placeholder={`Add a ${kind} idea…`} aria-label={`Add a ${kind} idea`}
                        onChange={e => { const v = e.target.value; setDraft(v); if (timer.current) window.clearTimeout(timer.current); timer.current = window.setTimeout(() => saveDraft(v), 700); }}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
                        onBlur={() => { if (timer.current) saveDraft(draft); }}
                        style={{ font: `500 14px ${FONT}`, color: INK, background: "transparent", border: "none", outline: "none", padding: "6px 0", minWidth: 0 }} />
                    <button type="button" onClick={commit} style={{ ...reset, font: `700 13px ${FONT}`, padding: "8px 16px", borderRadius: 999, background: C.sage, color: "#FFF" }}>Add</button>
                </div>
            </div>
            {err && <span style={{ fontSize: 12, color: C.alert }}>{err}</span>}
        </div>
    );
};

// ---------- the page ----------
export default function TodayDashboard() {
    const [params, setParams] = useSearchParams();
    const [day, setDay] = useState<Day | null>(null);
    const [err, setErr] = useState("");
    const [firstDone, setFirstDone] = useState(false);
    const [handled, setHandled] = useState<Set<string>>(new Set());
    const [rated, setRated] = useState<Record<string, string>>({});
    const [cat, setCat] = useState("All");
    const [openIdeas, setOpenIdeas] = useState<number | null>(null);
    const date = params.get("date");

    useEffect(() => {
        setDay(null); setErr(""); setCat("All");
        api.get("/founder-portal/briefs/", { params: date ? { date } : {} })
            .then(r => {
                setDay(r.data);
                setFirstDone(!!r.data.first_done);
                setHandled(new Set(r.data.handled || []));
                setRated(Object.fromEntries(Object.entries((r.data.ratings || {}) as Ratings).map(([k, v]) => [k, v.rating])));
            })
            .catch(e => setErr(e?.response?.status === 403 ? "Founder access required." : "Couldn't load the brief."));
    }, [date]);

    const onRated = useCallback((id: string, r: string) => setRated(cur => ({ ...cur, [id]: r })), []);

    const dates = day?.dates || [];
    const i = day?.date ? dates.indexOf(day.date) : -1;
    const prev = i > 0 ? dates[i - 1] : null;
    const next = i >= 0 && i < dates.length - 1 ? dates[i + 1] : null;
    const go = (d: string | null) => d && setParams({ date: d });

    const b: Brief = useMemo(() => day?.brief || {}, [day]);
    const n = day?.numbers || {};
    const pulse = n.pulse || {};
    const d = day?.date || "";

    const sources = useMemo(() => {
        const out: { name: string; ok: boolean; state: string }[] = [];
        Object.entries(day?.status?.sources || {}).forEach(([k, v]: any) => out.push({
            name: SOURCE_NAMES[k] || k, ok: !!v.ok,
            state: v.ok ? "loaded" : /timed? ?out/i.test(v.error || "") ? "read timed out" : "missing",
        }));
        (b.extra_loaded || []).forEach(name => out.push({ name, ok: true, state: "loaded" }));
        (b.extra_missing || []).forEach(name => out.push({ name, ok: false, state: "missing" }));
        return out.sort((x, y) => Number(y.ok) - Number(x.ok));
    }, [day, b.extra_loaded, b.extra_missing]);

    const news = useMemo(() => {
        const cats: NewsCategory[] = b.news || (b.radar?.stories?.length ? [{ category: "Radar", stories: b.radar.stories }] : []);
        return cats.flatMap(c => (c.stories || []).map(s => ({ s, category: c.category })));
    }, [b.news, b.radar]);
    const catNames = useMemo(() => Array.from(new Set(news.map(x => x.category))), [news]);
    const shownNews = cat === "All" ? news : news.filter(x => x.category === cat);
    const moments = useMemo(() => b.moments || [], [b.moments]);

    // Rated count for the nav: every story and moment on this page.
    const cardIds = useMemo(() => [...news.map(x => cardId(x.s.id, d)), ...moments.map(m => cardId(m.id, d))], [news, moments, d]);
    const ratedCount = cardIds.filter(id => rated[id]).length;

    const issueCount = useMemo(() => {
        if (!day) return 0;
        const ids = [...systemRows(day, b).map(r => r.id), ...((n.issues?.people || []) as Issue[]).map(p => personRow(p, d).id)];
        return ids.filter(id => !handled.has(id)).length;
    }, [day, b, n.issues, d, handled]);

    const health = (day?.health || (sources.some(s => !s.ok) ? "amber" : "green")).toLowerCase();
    const [hBg, hFg, hDot, hLabel] = health === "green" ? ["rgba(144,155,123,.22)", C.sageD, C.sage, "Green"]
        : health === "red" ? ["rgba(169,71,47,.12)", C.alert, C.alert, "Red"] : ["rgba(242,210,142,.4)", "#6B4E0E", AMBER, "Amber"];

    const toggleFirst = () => {
        if (!day?.date) return;
        const id = `${day.date}-first`;
        const done = !firstDone;
        setFirstDone(done);
        (done ? api.put(`/founder-portal/briefs/dismissals/${id}/`, { text: b.do_this_first?.text || "", link: b.do_this_first?.link || "" })
            : api.delete(`/founder-portal/briefs/dismissals/${id}/`)).catch(() => setFirstDone(!done));
    };
    const toggleHandled = (id: string) => {
        const was = handled.has(id);
        const flip = (on: boolean) => setHandled(cur => { const s = new Set(cur); if (on) s.add(id); else s.delete(id); return s; });
        flip(!was);
        (was ? api.delete(`/founder-portal/briefs/dismissals/${id}/`) : api.put(`/founder-portal/briefs/dismissals/${id}/`, { kind: "issue" }))
            .catch(() => flip(was));
    };

    const metric = (lbl: string, m?: Metric) => {
        if (!m) return { lbl, value: "–", color: "rgba(22,25,37,.22)", sub: <span style={{ color: C.faint }}>not loaded</span> };
        const pct = m.avg7 >= 10 ? `${m.delta > 0 ? "+" : "−"}${Math.round(Math.abs(m.delta) / m.avg7 * 100)}%` : `${m.delta > 0 ? "+" : m.delta < 0 ? "−" : ""}${Math.abs(m.delta)}`;
        const tone = m.signal ? (m.delta < 0 ? C.alert : C.sageD) : C.muted;
        return { lbl, value: String(m.value), color: INK, sub: <>avg {m.avg7} · <span style={{ fontWeight: 700, color: tone }}>{m.signal ? pct : "normal"}</span></> };
    };
    const subs = pulse.subscriptions;
    const pulseCells = [
        metric("Sign-ups", pulse.registrations), metric("Plans saved", pulse.programmes_saved),
        metric("Workouts done", pulse.workouts_completed), metric("Daily actives", pulse.daily_actives),
        subs ? { lbl: "Paying", value: String(subs.paying), color: INK, sub: <>{subs.in_trial} in trial</> }
            : { lbl: "Paying", value: "–", color: "rgba(22,25,37,.22)", sub: <span style={{ color: C.faint }}>not loaded</span> },
    ];
    const pulseBad = !n.pulse || ["registrations", "programmes_saved", "workouts_completed", "daily_actives"].some(k => pulse[k]?.signal && pulse[k].delta < 0);
    const pulseDay = n.day || (d ? dayBefore(d) : "");

    const navBtn = (to: string | null, text: string): React.ReactNode => (
        <button type="button" disabled={!to} onClick={() => go(to)} style={{
            ...reset, cursor: to ? "pointer" : "default", borderRadius: 999, padding: "8px 16px",
            font: `${to ? 700 : 600} 13px ${FONT}`, background: to ? "#FFF" : "transparent",
            border: `1.5px solid ${to ? INK : "rgba(22,25,37,.15)"}`, color: to ? INK : "rgba(22,25,37,.4)",
        }}>{text}</button>
    );
    const right = (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {navBtn(prev, `‹ ${prev ? shortDay(prev) : "Earlier"}`)}
            {navBtn(next, `${next ? shortDay(next) : "Later"} ›`)}
        </div>
    );
    const statusPill = day?.brief ? (
        <div style={{ display: "flex", gap: 7, alignItems: "center", font: `700 12px ${FONT}`, background: hBg, color: hFg, padding: "6px 12px", borderRadius: 999 }}>
            <Dot color={hDot} />{hLabel}
        </div>
    ) : null;

    const sections: [string, string, number | null, string][] = [
        ["Today", "#today", null, ""],
        ["Issues", "#issues", issueCount, issueCount ? C.alert : C.faint],
        ["News", "#news", news.length, C.faint],
        ["Moments", "#moments", moments.length, C.faint],
        ["Suggestions", "#suggestions", openIdeas, C.faint],
    ];

    const cohort = n.cohort;
    const health_ = n.health;
    const celery = n.celery;
    const waitDays = (w: Item) => { const m = (w.label || "").match(/\d+/); return m ? m[0] : null; };

    return (
        <Shell eyebrow="Daily · Morning brief" title={day?.date ? dayLabel(day.date) : "Today"} titleSize={40} titleAside={statusPill} right={right}>
            <style>{PAGE_CSS}</style>
            {err && <Empty>{err}</Empty>}
            {!err && !day && <Empty>Loading the brief…</Empty>}
            {day && !day.brief && <Empty>No brief for this day yet. The nightly run uploads it by about 05:30.</Empty>}
            {day?.brief && (
                <div className="tb" style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                    {/* sticky jump nav */}
                    <nav className="tb-sticky" style={{
                        position: "sticky", top: 0, zIndex: 5, background: C.cream, padding: "10px 0", margin: "-14px 0 -8px",
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", borderBottom: "1px solid rgba(22,25,37,.1)",
                    }}>
                        <div style={{ display: "flex", gap: 4, flexWrap: "nowrap" }}>
                            {sections.map(([l, href, c, color]) => (
                                <a key={l} href={href} className="hq-chip" onClick={e => { e.preventDefault(); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); }}>
                                    {l}{c !== null && <span style={{ font: `600 14px ${FONT_NUM}`, color }}>{c}</span>}
                                </a>
                            ))}
                        </div>
                        {!!cardIds.length && (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, font: `500 12px ${FONT}`, color: "rgba(22,25,37,.6)", whiteSpace: "nowrap" }}>
                                <div style={{ width: 120, height: 5, borderRadius: 5, background: "rgba(22,25,37,.1)" }}>
                                    <div style={{ height: 5, borderRadius: 5, background: C.sage, width: `${(ratedCount / cardIds.length) * 100}%` }} />
                                </div>
                                {ratedCount} of {cardIds.length} rated
                            </div>
                        )}
                    </nav>

                    {/* 1. Today */}
                    <section id="today" style={{ display: "flex", flexDirection: "column", gap: 20, scrollMarginTop: 64 }}>
                        <div className="tb-grid tb-today1">
                            {b.do_this_first && (
                                <div className="tb-dofirst" style={{
                                    minWidth: 0, background: "#FFF", border: `1.5px solid ${INK}`, borderRadius: 22, boxShadow: `5px 6px 0 ${INK}`,
                                    padding: "28px 32px", display: "flex", flexDirection: "column", gap: 18,
                                }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                                        <div style={label(C.sageD)}>Do this first</div>
                                        <div style={{ font: `500 12px ${FONT}`, color: "rgba(22,25,37,.5)" }}>{firstDone ? "Marked done" : b.do_this_first.chip || ""}</div>
                                    </div>
                                    <div className="tb-dofirst-text" style={{
                                        font: `700 26px/1.3 ${FONT}`, letterSpacing: "-.015em", maxWidth: 860,
                                        textDecoration: firstDone ? "line-through" : "none", color: firstDone ? "rgba(22,25,37,.4)" : INK,
                                    }}>{b.do_this_first.text}</div>
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                        <button type="button" onClick={toggleFirst} style={{
                                            ...reset, display: "flex", alignItems: "center", gap: 12, padding: "8px 8px 8px 20px", borderRadius: 999, font: `700 14px ${FONT}`,
                                            background: firstDone ? "#FFF" : C.sage, color: firstDone ? INK : "#FFF", border: `1.5px solid ${firstDone ? INK : C.sage}`,
                                        }}>
                                            {firstDone ? "Undo" : "Mark done"}
                                            <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#FFF", color: INK, display: "flex", alignItems: "center", justifyContent: "center", font: `700 13px ${FONT}`, border: "1px solid rgba(22,25,37,.15)" }}>{firstDone ? "↺" : "✓"}</span>
                                        </button>
                                        {!firstDone && b.do_this_first.link && (
                                            <a href={b.do_this_first.link} target="_blank" rel="noopener noreferrer" style={{
                                                display: "flex", alignItems: "center", gap: 12, padding: "8px 8px 8px 20px", borderRadius: 999, font: `700 14px ${FONT}`,
                                                background: "#FFF", color: INK, border: `1.5px solid ${INK}`, textDecoration: "none",
                                            }}>
                                                {b.do_this_first.link_label || "Open"}
                                                <span style={{ width: 28, height: 28, borderRadius: "50%", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", font: `700 13px ${FONT}` }}>→</span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div style={{ ...card, gap: 16 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                                    <div style={label()}>Today</div>
                                    <div style={{ font: `400 12px ${FONT}`, color: "rgba(22,25,37,.5)" }}>{b.today ? `${b.today.length} ${b.today.length === 1 ? "event" : "events"}` : "calendar not loaded"}</div>
                                </div>
                                {(b.today?.length ? b.today : [{ time: "–", title: "Nothing in the diary", note: "A clear day." }]).map((t, k) => (
                                    <div key={k} style={{ display: "grid", gridTemplateColumns: "64px minmax(0,1fr)", gap: 14, alignItems: "start" }}>
                                        <div style={{ ...numStyle(28), lineHeight: 1 }}>{t.time}</div>
                                        <div style={{ borderLeft: `3px solid ${C.sage}`, paddingLeft: 12, display: "flex", flexDirection: "column", gap: 3 }}>
                                            <div style={{ font: `700 15px ${FONT}` }}>{t.title}</div>
                                            {t.note && <div style={{ font: `400 13px ${FONT}`, color: "rgba(22,25,37,.6)" }}>{t.note}</div>}
                                        </div>
                                    </div>
                                ))}
                                {celery && (
                                    <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 14, borderTop: hair }}>
                                        <div style={label()}>Ops</div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, font: `600 13px ${FONT}` }}>
                                            <Dot color={celery.up ? C.sage : C.alert} size={8} />Celery
                                            <span style={{ fontWeight: 400, color: "rgba(22,25,37,.6)" }}>{celery.up ? "worker up" : `probe returned ${celery.status}`}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Business pulse strip */}
                        <div className="tb-pulse" style={{ ...card, padding: 0, display: "grid", overflow: "hidden" }}>
                            <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 6, justifyContent: "center" }}>
                                <div style={label("rgba(22,25,37,.55)", 10)}>Business pulse{pulseDay ? ` · ${shortDay(pulseDay)}` : ""} vs 7-day avg</div>
                                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", font: `500 13px/1.45 ${FONT}` }}>
                                    <Dot color={pulseBad ? AMBER : C.sage} style={{ marginTop: 6 }} />
                                    {b.pulse_read || (n.pulse ? "A normal day against the last week." : `App numbers didn't load overnight, so there's no read on ${pulseDay ? dayLabel(pulseDay, { weekday: "long" }) : "yesterday"}.`)}
                                </div>
                            </div>
                            {pulseCells.map(p => (
                                <div key={p.lbl} style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
                                    <div style={label("rgba(22,25,37,.55)", 10)}>{p.lbl}</div>
                                    <div style={{ ...numStyle(34), lineHeight: 1, color: p.color }}>{p.value}</div>
                                    <div style={{ font: `400 12px ${FONT}`, color: C.muted }}>{p.sub}</div>
                                </div>
                            ))}
                        </div>

                        {/* Needs you, waiting on, replies, new users, ops */}
                        <div className="tb-grid tb-today3">
                            {(b.needs || []).map((it, k) => {
                                const t = (it.label || it.tag || "fyi").toLowerCase();
                                const [bg, fg] = t === "decide" ? ["rgba(169,71,47,.1)", C.alert] : t === "reply" ? ["rgba(144,155,123,.18)", C.sageD] : [C.panel, "rgba(22,25,37,.75)"];
                                const inner = (
                                    <div style={{ ...card, gap: 10, height: "100%" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <span style={tagChip(bg, fg)}>{t}</span>
                                            <span style={label(C.faint, 10)}>Needs you</span>
                                        </div>
                                        <div style={{ font: `700 17px/1.3 ${FONT}` }}>{it.title}</div>
                                        {it.body && <div style={{ font: `400 14px/1.55 ${FONT}`, color: "rgba(22,25,37,.65)" }}>{it.body}</div>}
                                    </div>
                                );
                                return it.link
                                    ? <a key={k} href={it.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit", minWidth: 0 }}>{inner}</a>
                                    : <React.Fragment key={k}>{inner}</React.Fragment>;
                            })}

                            {!!b.waiting?.length && (
                                <div style={{ ...card, gap: 2 }}>
                                    <div style={{ ...label(), paddingBottom: 8 }}>Waiting on</div>
                                    {b.waiting.map((w, k) => {
                                        const days = waitDays(w);
                                        return (
                                            <div key={k} style={{ display: "grid", gridTemplateColumns: "48px minmax(0,1fr)", gap: 14, padding: "12px 0", borderTop: hair }}>
                                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", background: "rgba(169,71,47,.08)", borderRadius: 10, padding: "6px 0", color: C.alert, alignSelf: "start" }}>
                                                    <span style={{ ...numStyle(days ? 22 : 13, 700), lineHeight: 1 }}>{days || w.label || "–"}</span>
                                                    {days && <span style={{ font: `700 9px ${FONT}`, letterSpacing: ".1em" }}>DAYS</span>}
                                                </div>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                                                    <div style={{ font: `700 14px ${FONT}` }}>{w.link ? <a href={w.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>{w.title}</a> : w.title}</div>
                                                    {w.body && <div style={{ font: `400 13px/1.5 ${FONT}`, color: "rgba(22,25,37,.6)" }}>{w.body}</div>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {!!b.replies?.length && (
                                <div style={{ ...card, gap: 2 }}>
                                    <div style={{ ...label(), paddingBottom: 8 }}>Replies to send</div>
                                    {b.replies.map((r, k) => <Reply key={k} to={r.to} body={r.body} />)}
                                </div>
                            )}

                            {cohort && (
                                <div style={{ ...card, gap: 12 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                                        <div style={label()}>New users</div>
                                        <div style={{ font: `400 12px ${FONT}`, color: "rgba(22,25,37,.5)" }}>last 7 days</div>
                                    </div>
                                    {([["Joined", cohort.joined], ["Saved a plan", cohort.saved_plan], ["Finished a workout", cohort.finished_workout]] as [string, number][]).map(([l, v]) => (
                                        <div key={l} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 40px", gap: 12, alignItems: "end" }}>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                <div style={{ font: `600 12px ${FONT}`, color: "rgba(22,25,37,.6)" }}>{l}</div>
                                                <div style={{ height: 6, borderRadius: 6, background: C.track }}>
                                                    <div style={{ height: 6, borderRadius: 6, background: C.sage, width: `${(v / Math.max(cohort.joined, 1)) * 100}%` }} />
                                                </div>
                                            </div>
                                            <div style={{ ...numStyle(24), lineHeight: 1, textAlign: "right" }}>{v}</div>
                                        </div>
                                    ))}
                                    {(b.cohort_note || cohort.stuck?.length > 0) && (
                                        <div style={{ paddingTop: 12, borderTop: hair, font: `400 13px/1.5 ${FONT}`, color: "rgba(22,25,37,.7)" }}>
                                            <b style={{ color: INK }}>Worth a message:</b> {b.cohort_note || cohort.stuck.slice(0, 4).map((s: any) => `${s.id} (${s.why})`).join(", ")}
                                        </div>
                                    )}
                                </div>
                            )}

                            {health_ && (
                                <div style={{ ...card, gap: 2 }}>
                                    <div style={{ ...label(), paddingBottom: 8 }}>Ops</div>
                                    {([
                                        ["Pushes", !pushFailures(health_.notifications), pushNote(health_.notifications)],
                                        ["Garmin", !health_.garmin_reauth.flagged, health_.garmin_reauth.flagged ? `${health_.garmin_reauth.flagged} need reauth` : "No reauth flags"],
                                    ] as [string, boolean, string][]).map(([l, ok, note]) => (
                                        <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "11px 0", borderTop: hair }}>
                                            <span style={{ display: "flex", gap: 9, alignItems: "center", font: `600 13px ${FONT}` }}><Dot color={ok ? C.sage : AMBER} size={8} />{l}</span>
                                            <span style={{ font: `400 13px ${FONT}`, color: "rgba(22,25,37,.6)" }}>{note}</span>
                                        </div>
                                    ))}
                                    {b.ops_note && <div style={{ paddingTop: 10, borderTop: hair, font: `400 13px/1.5 ${FONT}`, color: "rgba(22,25,37,.7)" }}>{b.ops_note}</div>}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* 2. Issues */}
                    <Issues day={day} b={b} handled={handled} onHandle={toggleHandled} />

                    {/* 3. News */}
                    <section id="news" className="tb-section">
                        <SectionHead kicker="News" title="Worth knowing today"
                            right={<Aside>{(b.news_scanned ?? b.radar?.scanned) ? `Looked at ${b.news_scanned ?? b.radar?.scanned}, kept ${news.length}` : `${news.length} kept`} · rate as you read</Aside>} />
                        {news.length ? (
                            <>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    {["All", ...catNames].map(c => {
                                        const on = c === cat;
                                        return (
                                            <button key={c} type="button" onClick={() => setCat(c)} aria-pressed={on} style={{
                                                ...reset, padding: "7px 14px", borderRadius: 999, display: "flex", gap: 6, alignItems: "baseline",
                                                font: `${on ? 700 : 600} 13px ${FONT}`, background: on ? INK : "#FFF", color: on ? "#FFF" : INK,
                                                border: `1px solid ${on ? INK : "rgba(22,25,37,.15)"}`,
                                            }}>{c}<span style={{ font: `600 13px ${FONT_NUM}`, opacity: 0.7 }}>{c === "All" ? news.length : news.filter(x => x.category === c).length}</span></button>
                                        );
                                    })}
                                </div>
                                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                                    {shownNews.map(({ s, category }) => (
                                        <StoryRow key={cardId(s.id, d)} story={s} category={category} date={d} ratings={day.ratings || {}} onRated={onRated} />
                                    ))}
                                </div>
                            </>
                        ) : <Empty>No news in this brief.</Empty>}
                    </section>

                    {/* 4. Moments */}
                    <section id="moments" className="tb-section">
                        <SectionHead kicker="Moments from yesterday" title="Things worth turning into content"
                            right={d ? <Aside>Pulled from your chats and commits on {dayLabel(dayBefore(d), { day: "numeric", month: "short" })}</Aside> : null} />
                        {moments.length ? (
                            <div className="tb-grid tb-moments">
                                {moments.map(m => <MomentCard key={cardId(m.id, d)} story={m} date={d} ratings={day.ratings || {}} onRated={onRated} />)}
                            </div>
                        ) : <Empty>No moments picked out for this day.</Empty>}
                    </section>

                    {/* 5. Suggestions + sources */}
                    <section id="suggestions" className="tb-grid tb-sugs" style={{ paddingTop: 20, scrollMarginTop: 64 }}>
                        <Suggestions onOpenCount={setOpenIdeas} />
                        <aside className="tb-sources" style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 10, paddingTop: 62 }}>
                            <div style={label()}>Sources for this brief</div>
                            <div style={{ ...card, padding: "8px 20px" }}>
                                {sources.map((s, k) => (
                                    <div key={s.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: k < sources.length - 1 ? "1px solid rgba(22,25,37,.06)" : "none", font: `500 13px ${FONT}` }}>
                                        <span style={{ display: "flex", gap: 9, alignItems: "center" }}><Dot color={s.ok ? C.sage : C.alert} />{s.name}</span>
                                        <span style={{ font: `400 12px ${FONT}`, color: s.ok ? "rgba(22,25,37,.5)" : C.alert }}>{s.state}</span>
                                    </div>
                                ))}
                                {!sources.length && <div style={{ padding: "9px 0", font: `400 13px ${FONT}`, color: C.muted }}>No source list on this brief.</div>}
                            </div>
                        </aside>
                    </section>
                </div>
            )}
        </Shell>
    );
}
